import React, { useCallback, useEffect, useState } from 'react';
import { Col, Flex, Form, message, Row } from 'antd';
import { CustomButton, CustomInput, CustomTitle } from '../../components';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext';
import {
  GetSingleCourierById,
  LinkAccount,
  LinkAccountModel,
} from '../../apis/onboarding';
import useUnauthorize from '../../hooks/useUnauthorize';

interface ConnectFormModalData {
  id: string;
  instructions?: string;
  required_inputs?: string[];
  status?: 'pending' | 'linked' | 'basic' | 'error' | null;
}

interface ConnectFormModalProps {
  closeModal: () => void;
  data: ConnectFormModalData;
  onSuccess: () => void;
}

/**
 * Modal component for connecting courier accounts with dynamic form fields
 *
 * @param closeModal - Function to close the modal
 * @param data - Courier data including ID, instructions, and required inputs
 * @param onSuccess - Callback function called on successful connection
 */
const ConnectFormModal: React.FC<ConnectFormModalProps> = ({
  closeModal,
  data,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const { unauthorize } = useUnauthorize();
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [courierModal, setCourierModal] = useState(['Client ID']);

  //reset field when component opens
  useEffect(() => {
    if (data && data?.required_inputs && Array.isArray(data?.required_inputs)) {
      setCourierModal(data?.required_inputs);
    }
  }, [data]);

  const GetSingleCourier = useCallback(
    (id: string) => {
      showSpin();
      GetSingleCourierById(token, id)
        .then((res) => {
          const data = res?.data ? res.data : null;
          if (data) {
            if (data?.data?.is_success) {
              form.setFieldsValue(data?.data?.data?.couriers?.credentials);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [form, hideSpin, showSpin, token, unauthorize]
  );

  useEffect(() => {
    if (courierModal) {
      if (data?.id && (data?.status === 'linked' || data?.status === 'error')) {
        GetSingleCourier(data?.id);
      }
    }
  }, [GetSingleCourier, courierModal, data]);

  const sendFormData = (model: LinkAccountModel) => {
    showSpin();
    setIsFormValid(true);
    LinkAccount(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data.is_success) {
            closeModal();
            onSuccess();
            message.success(data?.data?.message);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((err) => {
        unauthorize(err);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  const onFinishForm = (values: Record<string, string>) => {
    if (values && Object.keys(values).length > 0 && data?.id) {
      const model: LinkAccountModel = {
        courier_id: data.id,
        credentials: { ...values },
        client_id: data.id,
        client_secret: data.id,
      };
      sendFormData(model);
    }
  };

  const handleCancel = () => {
    const model: LinkAccountModel = {
      courier_id: data?.id,
      credentials: null,
    };
    sendFormData(model);
  };

  return (
    <>
      <div>
        <CustomTitle
          text='Instructions'
          fontSize='14px'
          fontWeight='500'
          color='black'
        />
        <p
          dangerouslySetInnerHTML={{
            __html: data?.instructions,
          }}
        ></p>
      </div>

      <Form
        form={form}
        name='validateOnly'
        initialValues={{
          client_id: '', // Default initial value
          client_secret: '', // Default initial value
        }}
        layout='vertical'
        labelCol={{
          span: 16,
        }}
        wrapperCol={{
          span: 24,
        }}
        autoComplete='off'
        onFinish={onFinishForm}
      >
        <Row gutter={12}>
          <Col span={12}>
            {courierModal.map((element, index) => (
              <Form.Item
                key={element || index}
                label={element}
                name={element}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ paddingTop: '12px' }}
                rules={[
                  {
                    required: true,
                    message: `Please input ${element ? element : 'value'}`,
                  },
                ]}
              >
                <CustomInput
                  placeholder={`Paste ${element ? element : 'value'} here`}
                  height='36px'
                  width='100%'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            ))}
          </Col>
        </Row>

        <React.Fragment key='footer-actions'>
          <Flex gap='16px' justify='end' style={{ marginTop: '32px' }}>
            <CustomButton
              key='button1'
              onClick={handleCancel}
              text={"I'm okay with basic tracking"}
              bgColor={'#F3F3F3'}
              color='black'
              fontSize='14px'
              fontWeight='500'
              boxShadow='none'
              height='40px'
            />
            <CustomButton
              key='button2'
              text={'Confirm'}
              bgColor='black'
              color='white'
              height='40px'
              width='80px'
              fontSize='14px'
              fontWeight='500'
              boxShadow='none'
              borderRadius='8px'
              isFormValid={isFormValid}
              htmlType='submit'
            />
          </Flex>
        </React.Fragment>
      </Form>
    </>
  );
};

export default ConnectFormModal;
